import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Grid,
  Divider,
  Pagination
} from '@mui/material';
import dayjs from 'dayjs';

// Define types for WordPress post data
interface WordPressImage {
  id: number;
  source_url: string;
  alt_text: string;
}

interface WordPressPost {
  id: number;
  date: string;
  title: { rendered: string };
  excerpt: { rendered: string };
  content: { rendered: string };
  link: string;
  _embedded?: {
    'wp:featuredmedia'?: WordPressImage[];
  };
}

interface NewsArticlesProps {
  showFullView?: boolean;  // Whether to show the larger view (on News page)
  articlesPerPage?: number; // Number of articles to display per page
  maxArticles?: number;     // Max number of articles for homepage view
  hideViewAllButton?: boolean; // Hide View All button on the News page
}

const NewsArticles: React.FC<NewsArticlesProps> = ({
  showFullView = false,
  articlesPerPage = 4,
  maxArticles = 4,
  hideViewAllButton = false
}) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const WORDPRESS_API_URL = 'https://xyz.xaujournal.com/wp-json/wp/v2/posts';

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);

      try {
        // If on the homepage, only get limited number of articles
        // If on the news page, get paginated results
        const perPage = showFullView ? articlesPerPage : maxArticles;
        const pageParam = showFullView ? `&page=${currentPage}` : '';
        
        const response = await fetch(`${WORDPRESS_API_URL}?_embed&per_page=${perPage}${pageParam}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setPosts(data);
        
        // Get total number of pages for pagination
        if (showFullView) {
          // Only get the total pages, no need to store the total posts count
          const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1', 10);
          setTotalPages(totalPages);
        }
      } catch (err: any) {
        console.error("Failed to fetch blog posts:", err);
        setError(err.message || 'Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [showFullView, articlesPerPage, maxArticles, currentPage]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Navigate to article page
  const handleReadArticle = (postId: number) => {
    navigate(`/news/article/${postId}`);
  };

  // Safely extract HTML content and convert it to plain text
  const getExcerptText = (htmlContent: string) => {
    // Create a temporary element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Get text content (removes HTML tags)
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Truncate if too long
    if (text.length > 150) {
      text = text.substring(0, 147) + '...';
    }
    
    return text;
  };

  // Format the post date
  const formatPostDate = (dateStr: string) => {
    return dayjs(dateStr).format('MMM DD, YYYY');
  };

  // Get featured image URL
  const getFeaturedImageUrl = (post: WordPressPost) => {
    if (post._embedded && 
        post._embedded['wp:featuredmedia'] && 
        post._embedded['wp:featuredmedia'][0] &&
        post._embedded['wp:featuredmedia'][0].source_url) {
      return post._embedded['wp:featuredmedia'][0].source_url;
    }
    
    // Default image if no featured image is available
    return `${process.env.PUBLIC_URL}/assets/images/goldbar.png`;
  };

  return (
    <Box component="section" sx={{ mb: 6 }}>
      {!showFullView && (
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom color="secondary.main">
            Latest News
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Market insights and analysis from our blog
          </Typography>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress color="primary" />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && posts.length > 0 && (
        <Grid container spacing={3}>
          {posts.map(post => (
            <Grid item key={post.id} xs={12} md={showFullView ? 6 : 6} lg={showFullView ? 4 : 6}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <CardMedia
                  component="img"
                  height={showFullView ? "220" : "180"}
                  image={getFeaturedImageUrl(post)}
                  alt={post.title.rendered}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    {formatPostDate(post.date)}
                  </Typography>
                  <Typography variant="h5" component="h3" gutterBottom 
                    dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
                  <Typography variant="body2" color="text.secondary">
                    {getExcerptText(post.excerpt.rendered)}
                  </Typography>
                </CardContent>
                <Divider />
                <CardActions>
                  <Button 
                    onClick={() => handleReadArticle(post.id)}
                    size="small" 
                    color="primary"
                    sx={{ ml: 'auto' }}
                  >
                    Read More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Show pagination for full view mode */}
      {showFullView && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={totalPages} 
            page={currentPage} 
            onChange={handlePageChange} 
            color="primary"
            size="large"
          />
        </Box>
      )}

      {!loading && !error && posts.length === 0 && (
        <Typography sx={{ textAlign: 'center', mt: 3 }} color="text.secondary">
          No articles available at the moment.
        </Typography>
      )}

      {/* Show "View All Articles" button only on homepage */}
      {!loading && !error && posts.length > 0 && !showFullView && !hideViewAllButton && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => navigate('/news')}
          >
            View All Articles
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default NewsArticles;